import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { IntelligenceCard, IntelligenceCardStatus } from './IntelligenceCard';
import { useVipFamilies } from '@/hooks/useVipFamilies';

export const VipFamilyCard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useVipFamilies();

  // VIP families are always good news - success or neutral
  const getStatus = (count: number): IntelligenceCardStatus => {
    if (count === 0) return 'neutral';
    return 'success'; // VIP families deserve celebration!
  };

  // Handle appreciation action
  const handleAppreciateFamilies = () => {
    // Navigate to families/communication with VIP filter
    navigate('/admin/families', { 
      state: { 
        filterVip: true,
        suggestAppreciation: true
      } 
    });
  };

  const count = data?.count || 0;
  const status = getStatus(count);

  // Build context text
  const getContextText = () => {
    if (count === 0) {
      return "No standout families yet - keep building relationships! 🌱";
    }
    
    if (count === 1) {
      const family = data?.families[0];
      return `${family?.family_name}: ${family?.reason}`;
    }
    
    if (count <= 3) {
      const topFamily = data?.families[0];
      return `Led by ${topFamily?.family_name} with ${topFamily?.attendance_percentage}% attendance`;
    }
    
    return `${count} exceptional families setting the standard!`;
  };

  // Build status text
  const getStatusText = () => {
    if (count === 0) return 'Building community';
    if (count === 1) return '1 VIP family';
    return `${count} VIP families`;
  };

  return (
    <IntelligenceCard
      title="VIP Family Recognition"
      description="Excellent attendance & payments"
      icon={Crown}
      metric={count}
      metricLabel={count === 1 ? "VIP family" : "VIP families"}
      contextText={getContextText()}
      status={status}
      statusText={getStatusText()}
      actionLabel={count > 0 ? "Send Appreciation" : "View Families"}
      onAction={handleAppreciateFamilies}
      actionDisabled={false} // Always allow action
      isLoading={isLoading}
      error={error?.message}
    />
  );
};